const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/

const DAY_MS = 86400000

export const isIsoDate = (value: unknown): value is string => {
    if (typeof value !== 'string') return false

    const match = ISO_DATE.exec(value)

    if (!match) return false

    const year = Number(match[1])
    const month = Number(match[2])
    const day = Number(match[3])
    const date = new Date(Date.UTC(year, month - 1, day))

    return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
}

const toUtc = (iso: string): number => {
    const [year, month, day] = iso.split('-').map(Number)

    return Date.UTC(year, month - 1, day)
}

const fromUtc = (time: number): string => new Date(time).toISOString().slice(0, 10)

export const addDays = (iso: string, days: number): string => fromUtc(toUtc(iso) + days * DAY_MS)

export const diffDays = (from: string, to: string): number => Math.round((toUtc(to) - toUtc(from)) / DAY_MS)

export const eachNight = (checkIn: string, checkOut: string): Array<string> => {
    const nights: Array<string> = []
    const total = diffDays(checkIn, checkOut)

    for (let index = 0; index < total; index++) nights.push(addDays(checkIn, index))

    return nights
}

// Tunisia is UTC+1 all year round (no daylight saving time).
const TUNIS_OFFSET_MS = 3600000

export const tunisNow = (date: Date = new Date()): Date => new Date(date.getTime() + TUNIS_OFFSET_MS)

export const todayInTunis = (date: Date = new Date()): string => tunisNow(date).toISOString().slice(0, 10)

export const tunisMinutesOfDay = (date: Date = new Date()): number => {
    const local = tunisNow(date)

    return local.getUTCHours() * 60 + local.getUTCMinutes()
}

export const parseClock = (value: string, fallback: number): number => {
    const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim())

    if (!match) return fallback

    return Number(match[1]) * 60 + Number(match[2])
}

export const isWithinOpeningHours = (from: string, to: string, date: Date = new Date()): boolean => {
    const minutes = tunisMinutesOfDay(date)

    return minutes >= parseClock(from, 480) && minutes < parseClock(to, 1020)
}

export const monthKey = (iso: string): string => iso.slice(0, 7)

export const firstOfMonth = (iso: string): string => `${iso.slice(0, 7)}-01`

export const addMonths = (iso: string, months: number): string => {
    const [year, month] = iso.split('-').map(Number)
    const date = new Date(Date.UTC(year, month - 1 + months, 1))

    return fromUtc(date.getTime())
}

export const daysInMonth = (iso: string): number => {
    const [year, month] = iso.split('-').map(Number)

    return new Date(Date.UTC(year, month, 0)).getUTCDate()
}

// Monday = 0 ... Sunday = 6
export const weekdayIndex = (iso: string): number => (new Date(toUtc(iso)).getUTCDay() + 6) % 7

const pad = (value: number): string => String(value).padStart(2, '0')

export const formatDateNumeric = (iso: string): string => {
    if (!isIsoDate(iso)) return iso

    const [year, month, day] = iso.split('-')

    return `${day}.${month}.${year}`
}

export const formatTimestampTunis = (timestamp: string | null | undefined): string => {
    if (!timestamp) return ''

    const time = new Date(timestamp).getTime()

    if (Number.isNaN(time)) return ''

    const local = new Date(time + TUNIS_OFFSET_MS)

    return `${pad(local.getUTCDate())}.${pad(local.getUTCMonth() + 1)}.${local.getUTCFullYear()}, ${pad(local.getUTCHours())}:${pad(local.getUTCMinutes())}`
}

export const formatDateLocalized = (iso: string, monthsShort: Array<string>, pattern: string): string => {
    if (!isIsoDate(iso)) return iso

    const [year, month, day] = iso.split('-').map(Number)

    return pattern
        .replace('{d}', String(day))
        .replace('{dd}', pad(day))
        .replace('{mon}', monthsShort[month - 1] ?? '')
        .replace('{y}', String(year))
}
